import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, DatePicker, message } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import GenericDatePicker from "../../../Components/GenericDatePicker";
import locationIcon from "../../../Assets/Icons/location.svg";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { getCallLogs } from "../../../API/User/calllogs";
import { useDateFilter } from "../../../Contexts/DateFilterContext";

const CallLogs = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [callLogs, setCallLogs] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const { date, setDate } = useDateFilter();

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const successNotification = (msg) => {
    messageApi.success(msg);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };
  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };
  const customerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Vehicle Name",
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle_name"),
    },
    {
      title: "Reciever's Phone Number",
      dataIndex: "to_phone_number",
      key: "to_phone_number",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("to_phone_number"),
    },
    {
      title: "Dialer's Phone Number",
      dataIndex: "from_phone_number",
      key: "from_phone_number",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("from_phone_number"),
    },
    {
      title: "Company Number",
      dataIndex: "company_number",
      key: "company_number",
    },

    {
      title: "Call Duration",
      dataIndex: "duration",
      key: "duration",
      sorter: (a, b) => (a.duration || 0) - (b.duration || 0),
      render: (value) => {
        if (value === null || value === undefined) return "0s";
        return `${value}s`;
      },
    },

    {
      title: "Date & Time",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (date) => date,
    },

    {
      title: "Secret Pin",
      dataIndex: "is_secret_matched",
      key: "is_secret_matched",
      render: (value) => {
        let label = "Call Cut Before Pin Added";

        if (value === true) label = "Pin Matched";
        else if (value === false) label = "Pin Not Matched";

        return (
          <span
            className={`pin-badge ${label.toLowerCase().replace(/ /g, "-")}`}
          >
            {label}
          </span>
        );
      },
      filters: [
        { text: "Pin Matched", value: true },
        { text: "Pin Not Matched", value: false },
      ],
      onFilter: (value, record) => record.is_secret_matched === value,
    },
    {
      title: "Theft Status",
      dataIndex: "theft_status",
      key: "theft_status",
      render: (status) => {
        const safeStatus = status || "none";

        const formatted =
          safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1);

        return (
          <span
            className={`result-badge ${safeStatus
              .toLowerCase()
              .replace(/ /g, "-")}`}
          >
            {formatted}
          </span>
        );
      },
      filters: [
        { text: "Unverified", value: "unverified" },
        { text: "Confirmed", value: "confirmed" },
        { text: "Safe", value: "safe" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Results",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const safeStatus = status || "unknown";

        const formatted =
          safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1);

        return (
          <span
            className={`result-badge ${safeStatus
              .toLowerCase()
              .replace(/ /g, "-")}`}
          >
            {formatted}
          </span>
        );
      },
      filters: [
        { text: "Answered", value: "answered" },
        { text: "Unanswered", value: "unanswered" },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ];

  const fetchData = async (selectedDate) => {
    try {
      if (!selectedDate) {
        setCallLogs([]);
        return;
      }
      setLoading(true);

      const formatted = selectedDate.format("YYYY-MM-DD");

      const res = await getCallLogs(formatted, formatted);

      const raw = res?.data;

      const arr = Array.isArray(raw) ? raw : raw?.customers || [];

      setCallLogs(arr);
    } catch (error) {
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDate(dayjs());
  }, []);

  useEffect(() => {
    if (date) {
      fetchData(date);
    }
  }, [date]);

  return (
    <div className="call-log">
      {contextHolder}
      <div className="date-btn">
        <GenericDatePicker
          value={date}
          onChange={(val) => setDate(val ? dayjs(val) : null)}
        />
      </div>
      <div className="table">
        <GenericTable
          dataSource={callLogs}
          columns={customerColumns}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CallLogs;
